import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { get, post } from './api';
import { signOut, type Launch } from './auth';
import { PERSON_UNIT, ROLE_LBL } from './lib';
import type { CalEvent, Catalog, Me } from './types';
import { ThemeToggle, useFail, useToast } from './ui';
import QuickBooking from './booking/QuickBooking';
import KeysView from './views/KeysView';
import VisitorsView from './views/VisitorsView';
import NotebooksView from './views/NotebooksView';
import NbAdmView from './views/NbAdmView';
import ManageView from './views/ManageView';
import ReportsView from './views/ReportsView';
import SecNbView from './views/SecNbView';
import LibraryView from './views/LibraryView';
import CalendarView from './views/CalendarView';

interface AppData { me: Me; catalog: Catalog; events: CalEvent[]; roomById: (id: string) => Catalog['rooms'][number] | undefined; reloadEvents: () => void }
const DataCtx = createContext<AppData | null>(null);
export const useData = () => useContext(DataCtx)!;

type View = 'book' | 'keys' | 'secnb' | 'visitors' | 'notebooks' | 'nbadm' | 'library' | 'manage' | 'calendar' | 'reports';
// Áreas por tipo de trabalho; cada pessoa só vê as áreas e seções do próprio perfil
const AREAS: { id: string; label: string; views: [View, string][] }[] = [
  { id: 'reservas', label: 'Reservas', views: [['book', 'Reservar salas']] },
  // A Gestão de chaves é uma aba dentro de Chaves
  { id: 'secretaria', label: 'Secretaria', views: [['keys', 'Chaves'], ['secnb', 'Notebooks'], ['visitors', 'Visitantes']] },
  { id: 'tec', label: 'Tecnologia', views: [['notebooks', 'Notebooks da TI'], ['nbadm', 'Cadastro de notebooks']] },
  { id: 'biblioteca', label: 'Biblioteca', views: [['library', 'Empréstimos']] },
  { id: 'gestao', label: 'Gestão', views: [['manage', 'Gestão de reservas'], ['calendar', 'Calendário escolar']] },
  { id: 'relatorios', label: 'Relatórios', views: [['reports', 'Relatórios']] },
];

export default function App({ launch }: { launch: Launch }) {
  const [data, setData] = useState<AppData | null>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>('book');
  const [lastView, setLastView] = useState<Record<string, View>>({});
  const [mineVersion, setMineVersion] = useState(0);
  const toast = useToast(), fail = useFail();

  useEffect(() => {
    Promise.all([get<Me>('me'), get<Catalog>('rooms'), get<CalEvent[]>('calendar')])
      .then(([me, catalog, events]) => setData({ me, catalog, events, roomById: id => catalog.rooms.find(r => r.id === id),
        // Depois de mudar o calendário escolar, as telas de reserva passam a usar as datas novas
        reloadEvents: () => { get<CalEvent[]>('calendar').then(ev => setData(d => d && { ...d, events: ev })).catch(() => undefined); } }))
      .catch(() => setError('Não foi possível carregar o Convés. Recarregue a página; se continuar, avise a TI.'));
  }, []);

  // Link vindo do aviso do Teams: "confirmar=123" confirma a presença direto
  useEffect(() => {
    if (!data || !launch.deepLink) return;
    // "v=notebooks": aviso de falta de notebooks para a TI abre direto em Notebooks da TI
    if (launch.deepLink === 'v=notebooks' && can('notebooks')) { setView('notebooks'); return; }
    const m = /^confirmar=(\d+)$/.exec(launch.deepLink);
    if (m) post(`bookings/${m[1]}/presence`, { action: 'confirm' })
      .then(() => { toast('Presença confirmada. A sala está garantida para você.'); setMineVersion(v => v + 1); })
      .catch(fail);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sem a barra de reserva fixa embaixo, as outras áreas não precisam do espaço extra no fim da página
  useEffect(() => { document.body.classList.toggle('noact', view !== 'book'); }, [view]);

  const can = (v: View) => !data ? v === 'book' : v === 'book' || (data.me.can as Record<string, boolean>)[v];
  const areas = useMemo(() => AREAS.filter(a => a.views.some(([v]) => can(v))), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <div className="boot"><p>{error}</p></div>;
  if (!data) return <div className="boot"><img src="/conves-logo-sobre-azul.png" alt="" width="176" height="110" /><p>Abrindo o Convés…</p></div>;

  const cur = AREAS.find(a => a.views.some(([v]) => v === view))!;
  const subs = cur.views.filter(([v]) => can(v));
  const go = (v: View) => { setView(v); setLastView(l => ({ ...l, [AREAS.find(a => a.views.some(([x]) => x === v))!.id]: v })); };
  const me = data.me;
  // Para a TI, a área Secretaria só tem as chaves: aparece como "Chaves"
  const areaLabel = (a: { id: string; label: string }) => a.id === 'secretaria' && me.roles.includes('ti') && !me.roles.includes('admin') && !me.can.visitors ? 'Chaves' : a.label;
  const roleTxt = me.roles.filter(r => r !== 'colab').map(r => ROLE_LBL[r]).join(', ') || ROLE_LBL.colab;

  return (
    <DataCtx.Provider value={data}>
      <header className="top">
        <div className="in">
          <div className="brand">
            <img className="logo-img" src="/logo-colegio.png" alt="Colégio Catamarã · Ensino Bilíngue" width="129" height="60" />
            <div className="app-name"><h1>Convés</h1><p>Espaços e recursos do Catamarã</p></div>
          </div>
          <div className="top-right">
            <ThemeToggle />
            <div className="user" title={me.upn}>
              <div className="avatar">{(w => (w[0][0] + (w.length > 1 ? w[w.length - 1][0] : '')).toUpperCase())(me.name.split(/\s+/).filter(Boolean))}</div>
              <div><b>{me.name}</b><small>{roleTxt}{me.unit ? ' · ' + (PERSON_UNIT[me.unit] ?? me.unit) : ''}{launch.inTeams ? ' · via Teams' : ''}</small></div>
            </div>
            {!launch.inTeams && (
              <button type="button" className="bell" aria-label="Sair do Convés" title="Sair" onClick={() => { void signOut(); }}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <nav className="tabs" role="tablist" aria-label="Áreas">
          {areas.map(a => (
            <button key={a.id} role="tab" aria-selected={a === cur} onClick={() => {
              const vis = a.views.filter(([v]) => can(v));
              go(vis.some(([v]) => v === lastView[a.id]) ? lastView[a.id] : vis[0][0]);
            }}>{areaLabel(a)}</button>
          ))}
        </nav>
      </header>
      {subs.length > 1 && (
        <nav className="subnav" aria-label="Seções da área">
          <span className="lbl">{areaLabel(cur)}</span>
          <div className="seg" role="group" aria-label={`Seções de ${areaLabel(cur)}`}>
            {subs.map(([v, l]) => <button key={v} type="button" aria-pressed={v === view} onClick={() => go(v)}>{l}</button>)}
          </div>
        </nav>
      )}
      {!me.unit && (
        <div className="proto"><span>Sua unidade ainda não está cadastrada no Entra ID. Enquanto isso, as salas pedagógicas e os notebooks da TI não aparecem para você. Peça à TI para atualizar o seu cadastro.</span></div>
      )}
      {view === 'book' && <QuickBooking mineVersion={mineVersion} />}
      {view === 'keys' && <KeysView />}
      {view === 'visitors' && <VisitorsView />}
      {view === 'notebooks' && <NotebooksView />}
      {view === 'nbadm' && <NbAdmView />}
      {view === 'manage' && <ManageView />}
      {view === 'calendar' && <CalendarView />}
      {view === 'reports' && <ReportsView />}
      {view === 'secnb' && <SecNbView />}
      {view === 'library' && <LibraryView />}
    </DataCtx.Provider>
  );
}
